import { WhichWeightAPI } from 'WhichWeightAPI'
import { useState, useEffect, useContext, useRef } from 'react'
import { ModalContext } from '../ModalContext'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { useGetExercises } from 'redux/customHooks/useGetExercises'

import MediumLoadingComponent from 'components/Loading/MediumLoadingComponent'

// Exercise swap type options
const exerciseSwapTypeOptions = [
    {
        'name': 'Mobility',
        'key': 'mobility-swap'
    },
    {
        'name': 'At-Home',
        'key': 'at-home-swap'
    },
    {
        'name': 'Entire Library',
        'key': 'entire-library-swap'
    }
]

// Mobility exercise swap locations
const mobilityExerciseSwapLocations = [
    { name: 'Adductors', key: 'adductors' },
    { name: 'Ankles', key: 'ankles' },
    { name: 'Glutes', key: 'glute-max' },
    { name: 'Hamstrings', key: 'hamstrings' },
    { name: 'Hip Flexors', key: 'hip-flexors' },
    { name: 'ITB / TFL', key: 'itb' },
    { name: 'Quads', key: 'quads' },
    { name: 'Shoulders / Chest', key: 'shoulders' },
    { name: 'Thoracic Spine', key: 'thoracic-spine' },
]

export default function ExerciseSwapModal() {
    const { modalData, HideModal } = useContext(ModalContext)

    const { exercises } = useGetExercises()
    
    const [selectedSwapType, setSelectedSwapType] = useState(exerciseSwapTypeOptions[0].key)
    const [selectedMobilityLocation, setSelectedMobilityLocation] = useState(null)
    const [listLoading, setListLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [listItems, setListItems] = useState([])

    // Request cancellation and tracking
    const abortControllerRef = useRef(null)
    const currentRequestIdRef = useRef(0)

    const OnSwapTypeSelected = (value) => {
        if (value === 'mobility-swap') setSelectedMobilityLocation(null) // Reset mobility location when swap type is changed
        setSelectedSwapType(value)
        
        // Clear the list immediately when switching tabs for better UX
        setListItems([])
    }

    const InitializeList = async () => {
        // Cancel any ongoing request
        if (abortControllerRef.current) abortControllerRef.current.abort()

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController()
        
        // Increment request ID to track the most recent request
        const requestId = ++currentRequestIdRef.current

        switch (selectedSwapType) {

            case 'mobility-swap':

                if (selectedMobilityLocation === null) {
                    setListItems(mobilityExerciseSwapLocations); 
                }

                if (selectedMobilityLocation !== null) {
                    setListLoading(true)

                    try {

                        const jsonBody = {
                            swapType: 'mobility',
                            bodyLocation: selectedMobilityLocation,
                        }
                
                        const result = await WhichWeightAPI('CORE', 'GET', '/exercise_swap_options', jsonBody, abortControllerRef.current.signal) 
                        
                        // Only update state if this is still the most recent request
                        if (requestId === currentRequestIdRef.current) {
                            setListItems(result.data.data)
                        }

                    } catch (error) {
                        // Only handle errors if this is still the most recent request and it wasn't aborted
                        if (requestId === currentRequestIdRef.current && error.name !== 'AbortError' && error.name !== 'CanceledError') {
                            console.error(error)
                            setListItems([])
                        }
                    }

                    // Only update loading state if this is still the most recent request
                    if (requestId === currentRequestIdRef.current) {
                        setListLoading(false)
                    }
                }

                break;

            case 'at-home-swap': 
                setListLoading(true)

                try {

                    const jsonBody = {
                        swapType: 'home',
                        exerciseId: modalData.currentExercise.id,
                    }

                    const result = await WhichWeightAPI('CORE', 'GET', '/exercise_swap_options', jsonBody, abortControllerRef.current.signal)

                    // Only update state if this is still the most recent request
                    if (requestId === currentRequestIdRef.current) {
                        setListItems(result.data.data)
                    }

                } catch (error) {
                    // Only handle errors if this is still the most recent request and it wasn't aborted
                    if (requestId === currentRequestIdRef.current && error.name !== 'AbortError' && error.name !== 'CanceledError') {
                        console.error(error)
                        setListItems([])
                    }
                }

                // Only update loading state if this is still the most recent request
                if (requestId === currentRequestIdRef.current) {
                    setListLoading(false)
                }
                
                break;

            case 'entire-library-swap': 

                setListItems(exercises); 

                break;

            default: 

                setListItems([]); 

                break;
        }
    }

    const OnListItemSelected = async (item) => {
        switch (selectedSwapType) {
            case 'mobility-swap':
                if (selectedMobilityLocation === null) {
                    setSelectedMobilityLocation(item.key)
                    // Clear the list immediately when selecting a mobility location for better UX
                    setListItems([])
                }

                if (selectedMobilityLocation !== null) {
                    modalData.selectAction(selectedSwapType, item)
                    HideModal()
                }

                break

            case 'at-home-swap':
                modalData.selectAction(selectedSwapType, item)
                HideModal()
                break

            case 'entire-library-swap':
                modalData.selectAction(selectedSwapType, item)
                HideModal()
                break
        }
    }

    useEffect(() => {
        if (
            modalData.phaseKey === 'main-1' || 
            modalData.phaseKey === 'main-2'
        ) {
            setSelectedSwapType(exerciseSwapTypeOptions[1].key)
        }
    }, [modalData.phaseKey])

    useEffect(() => { 
        setListLoading(false) // Reset loading state before initializing new list
        InitializeList() 
    }, [selectedSwapType, selectedMobilityLocation])

    // Cleanup function to abort any ongoing requests when component unmounts
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
        }
    }, [])

    return (
        <>
            <h2>Swap {modalData.currentExercise.name}</h2>

            <p className='text-xs'>What type of exercise swap are you looking for?</p>

            {/* Swap Type Selector */}
            <div>
                <div className='h-12 items-center grid grid-cols-3 rounded-full divide-x-2 divide-white overflow-hidden text-center bg-gray-100'>
                    {exerciseSwapTypeOptions.map((option) => (
                        <div 
                            key={option.key}
                            className={`
                                h-12 flex items-center justify-center text-xs cursor-pointer
                                ${selectedSwapType === option.key ? 'text-white font-bold' : 'text-gray-500'}
                            `}
                            style={{ backgroundColor: selectedSwapType === option.key ? '#f97316' : '' }}
                            onClick={() => OnSwapTypeSelected(option.key)}
                        >
                            {option.name}
                        </div>
                    ))}
                </div>
            </div>

            {/* Search Bar */}
            <div className='relative'>
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <input
                    type="text"
                    className="pl-10 w-full h-12 rounded-full bg-gray-100 px-4 text-gray-900 placeholder:text-gray-400 focus:outline-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                    placeholder='Search Here'
                    onChange={(event) => setSearchTerm(event.target.value)}
                />
            </div>
            
            {listLoading && <MediumLoadingComponent />}

            {!listLoading && 
                <div 
                    className={`
                        bg-gray-100 rounded-xl overflow-y-auto divide-y divide-gray-300 max-h-[60vh]
                        [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]
                    `}
                >
                    {listItems.filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && (
                        <p className='p-4 text-gray-400'>
                            {selectedSwapType === 'mobility-swap' ? 'No exercises found' : ''}
                            {selectedSwapType === 'at-home-swap' ? 'No at-home alternatives found' : ''}
                            {selectedSwapType === 'entire-library-swap' ? 'No exercises found' : ''}
                        </p>
                    )}

                    {listItems.filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase())).length > 0 && (
                        <>
                            {listItems.filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase())).map((item, index) => (
                                <p 
                                    key={index} 
                                    onClick={() => OnListItemSelected(item)}
                                    className="flex p-4 text-left cursor-pointer hover:bg-gray-200"
                                >
                                    {item.name}
                                </p>
                            ))}
                        </>
                    )}
                </div>
            }
        </>
    )
}
